import { createSlice } from '@reduxjs/toolkit';
import {
  US_STATES,
  ownershipModelOptions,
  equipmentTypeOptions,
  paymentTypeOptions,
  publicChargingOptions,
  chargingAtHomeOptions,
} from './inputOptions';

const selectValidValues = {
  state: new Set(US_STATES.map((o) => o.value)),
  ownershipModel: new Set(ownershipModelOptions.map((o) => o.value)),
  equipmentType: new Set(equipmentTypeOptions.map((o) => o.value)),
  chargingType: new Set(paymentTypeOptions),
  openToPublic: new Set(publicChargingOptions),
  chargingAtHome: new Set(chargingAtHomeOptions.map((o) => o.value)),
};

const initialState = {
  value: {
    numOfEmployees: null,
    avgDailyAttenance: 90,
    carShare: 60,
    evShare: 20,
    parkingSpaces: null,
    existingChargers: 0,
    chargingPorts: null,
    chargingAtHome: 1,
    ownershipModel: 'Direct Ownership',
    equipmentType: 'Level 2 - Networked',
    chargingType: 'Paid',
    openToPublic: 'Yes',
    stateIncentive: null,
    utilityIncentive: null,
    city: null,
    state: null,
    zip: null,
    streetAddress: null,
    chargingFee: 0,
  },
  valid: true,
  invalidMessages: [],
  invalidFields: [],
};

const inputTypesRequired = {
  numOfEmployees: ['number', true],
  avgDailyAttenance: ['number', true],
  carShare: ['number', true],
  evShare: ['number', true],
  parkingSpaces: ['number', false],
  existingChargers: ['number', true],
  chargingPorts: ['number', true],
  chargingAtHome: ['number', true],
  ownershipModel: ['string', true],
  equipmentType: ['string', true],
  chargingType: ['string', true],
  openToPublic: ['string', true],
  stateIncentive: ['number', false],
  utilityIncentive: ['number', false],
  city: ['string', false],
  state: ['string', true],
  zip: ['string', false],
  streetAddress: ['string', false],
};

const prettyLabels = {
  numOfEmployees: 'Total Employees',
  avgDailyAttenance: 'Avg Daily Attendance',
  carShare: 'Car Commuter Share',
  evShare: 'EV Commuter Share',
  parkingSpaces: 'Total Parking Spaces',
  existingChargers: 'Existing Charging Ports',
  chargingPorts: 'Charging Ports to Install',
  chargingAtHome: 'Employees with Home Charging',
  ownershipModel: 'Ownership Model',
  equipmentType: 'Charger Type',
  chargingType: 'Employee Charging Cost',
  openToPublic: 'Open to the Public',
  stateIncentive: 'State Incentive Amount',
  utilityIncentive: 'Utility Incentive Amount',
  city: 'City',
  state: 'State',
  zip: 'Zip Code',
  streetAddress: 'Street Address',
};

const validateInputs = (state) => {
  const invalidMessages = [];
  const invalidFields = [];
  let inputsValid = true;

  for (const [key, value] of Object.entries(state.value)) {
    if (!(key in inputTypesRequired)) {
      continue;
    }

    if (inputTypesRequired[key][1] && (value === null || value === '')) {
      inputsValid = false;
      invalidMessages.push(`${prettyLabels[key]} is required`);
      invalidFields.push(key);
      continue;
    }

    if (!(typeof value === inputTypesRequired[key][0] || !value)) {
      inputsValid = false;
      invalidMessages.push(`${prettyLabels[key]} input type is incorrect`);
      invalidFields.push(key);
      continue;
    }

    if (inputTypesRequired[key][0] === 'number' && value < 0) {
      inputsValid = false;
      invalidMessages.push(`${prettyLabels[key]} must be non-negative`);
      invalidFields.push(key);
      continue;
    }
  }

  if (state.value.numOfEmployees !== null && state.value.numOfEmployees < 1) {
    inputsValid = false;
    invalidMessages.push('Number of Employees must be at least 1');
    invalidFields.push('numOfEmployees');
  }

  const maxNewPorts = 100 - (state.value.existingChargers ?? 0);
  if (
    state.value.chargingPorts !== null &&
    (state.value.chargingPorts < 1 || state.value.chargingPorts > maxNewPorts)
  ) {
    inputsValid = false;
    invalidMessages.push(
      state.value.existingChargers
        ? 'Charging Ports to Install plus Existing Charging Ports cannot exceed 100'
        : 'Number of Charging Ports must be between 1 and 100',
    );
    invalidFields.push('chargingPorts');
  }

  if (
    state.value.existingChargers !== null &&
    state.value.parkingSpaces !== null &&
    state.value.existingChargers > state.value.parkingSpaces
  ) {
    inputsValid = false;
    invalidMessages.push(
      'Existing Charging Ports cannot exceed Total Parking Spaces',
    );
    invalidFields.push('existingChargers');
  }

  for (const [field, validSet] of Object.entries(selectValidValues)) {
    if (
      state.value[field] != null &&
      !validSet.has(state.value[field]) &&
      !invalidFields.includes(field)
    ) {
      inputsValid = false;
      invalidMessages.push(`${prettyLabels[field]} has an invalid selection`);
      invalidFields.push(field);
    }
  }

  state.valid = inputsValid;
  state.invalidMessages = invalidMessages;
  state.invalidFields = invalidFields;
};

validateInputs(initialState);

export const inputs = createSlice({
  name: 'inputs',
  initialState: initialState,
  reducers: {
    populateInputs: (state, action) => {
      state.value = action.payload;

      if (state.value.equipmentType.includes('Non-networked')) {
        state.value.chargingType = 'Free';
        state.value.chargingFee = 0;
      }

      validateInputs(state);
    },
    updateChargingFee: (state, action) => {
      state.value.chargingFee = action.payload;
      state.value.chargingType = action.payload > 0 ? 'Paid' : 'Free';
    },
    resetEnableRecalculate: (state, action) => {
      state.enableRecalculate = action.payload;
    },
    resetEnableReset: (state, action) => {
      state.enableReset = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  populateInputs,
  updateChargingFee,
  resetEnableRecalculate,
  resetEnableReset,
} = inputs.actions;

export default inputs.reducer;
